/**
 * APR for advertised rates (public landing page).
 *
 * Uses the actuarial method (Reg Z Appendix J): the APR is the rate that
 * discounts every scheduled payment back to the amount financed. That is not
 * what `calculateAPRWithFees` in aprUtils does (it approximates with
 * rate + fees ÷ years and ignores monthly mortgage insurance), so advertised
 * figures use this instead.
 *
 * Every number comes from the program's published `aprAssumptions` plus the
 * representative scenario below, which the landing page discloses.
 */

export interface PublishedAprAssumptions {
  originationPercent?: number;
  pointsPercent?: number;
  underwritingFee?: number;
  processingFee?: number;
  applicationFee?: number;
  creditReportFee?: number;
  lenderFees?: number;
  otherFees?: number;
  prepaidInterestDays?: number;
  fhaUfmipPercent?: number;
  fhaAnnualMIPPercent?: number;
  vaFundingFeePercent?: number;
  usdaUpfrontGuaranteeFeePercent?: number;
  usdaAnnualFeePercent?: number;
}

export type AdvertisedLoanShape =
  | { kind: 'amortizing'; termMonths: number }
  | { kind: 'interestOnly'; termMonths: number }
  /** Open-end credit (HELOC): Reg Z APR is the periodic rate, fees excluded. */
  | { kind: 'openEnd' };

/** Representative loan used to turn flat fees into a share of the loan. */
export const REPRESENTATIVE_LOAN_AMOUNT = 300_000;

/** Loan shape assumed for each program's advertised APR (disclosed on page). */
export const ADVERTISED_LOAN_SHAPE: Record<string, AdvertisedLoanShape> = {
  conventional: { kind: 'amortizing', termMonths: 360 },
  fha: { kind: 'amortizing', termMonths: 360 },
  va: { kind: 'amortizing', termMonths: 360 },
  usda: { kind: 'amortizing', termMonths: 360 },
  dscr: { kind: 'amortizing', termMonths: 360 },
  commercial: { kind: 'amortizing', termMonths: 360 },
  heloan: { kind: 'amortizing', termMonths: 360 },
  secondMortgage: { kind: 'amortizing', termMonths: 360 },
  fixAndFlip: { kind: 'interestOnly', termMonths: 12 },
  bridge: { kind: 'interestOnly', termMonths: 12 },
  heloc: { kind: 'openEnd' },
};

const pct = (v?: number) => (Number.isFinite(v) ? (v as number) / 100 : 0);
const num = (v?: number) => (Number.isFinite(v) ? (v as number) : 0);

/**
 * Advertised APR (percent) for a program, or null when it can't be computed.
 * Upfront government fees (FHA UFMIP, VA funding fee, USDA guarantee fee) are
 * treated as financed into the note; annual MIP / USDA fees are charged
 * monthly on the outstanding balance for the full term.
 */
export function advertisedApr(
  ratePercent: number,
  shape: AdvertisedLoanShape | undefined,
  a: PublishedAprAssumptions = {}
): number | null {
  if (!shape || !Number.isFinite(ratePercent) || ratePercent <= 0) return null;
  if (shape.kind === 'openEnd') return ratePercent;

  const base = 1; // work per $1 of base loan
  const r = ratePercent / 100 / 12;
  const n = shape.termMonths;

  const financedFee =
    pct(a.fhaUfmipPercent) +
    pct(a.vaFundingFeePercent) +
    pct(a.usdaUpfrontGuaranteeFeePercent);
  const note = base * (1 + financedFee);

  const flatFees =
    num(a.underwritingFee) +
    num(a.processingFee) +
    num(a.applicationFee) +
    num(a.creditReportFee) +
    num(a.lenderFees) +
    num(a.otherFees);
  const prepaidFinanceCharges =
    pct(a.originationPercent) * base +
    pct(a.pointsPercent) * base +
    flatFees / REPRESENTATIVE_LOAN_AMOUNT +
    num(a.prepaidInterestDays) * (ratePercent / 100 / 365) * base;
  const amountFinanced = base - prepaidFinanceCharges;
  if (amountFinanced <= 0) return null;

  const monthlyMi =
    (pct(a.fhaAnnualMIPPercent) + pct(a.usdaAnnualFeePercent)) / 12;

  // Scheduled payments on the note.
  const payments: number[] = [];
  let balance = note;
  const pi =
    shape.kind === 'amortizing'
      ? r === 0
        ? note / n
        : (note * r) / (1 - Math.pow(1 + r, -n))
      : 0;
  for (let k = 1; k <= n; k++) {
    const mi = balance * monthlyMi;
    if (shape.kind === 'amortizing') {
      const interest = balance * r;
      balance -= pi - interest;
      payments.push(pi + mi);
    } else {
      const last = k === n;
      payments.push(balance * r + mi + (last ? balance : 0));
    }
  }

  // Solve PV(payments, i/12) = amountFinanced by bisection (monotone in i).
  const pv = (annual: number) => {
    const m = annual / 12;
    let sum = 0;
    let disc = 1;
    for (const p of payments) {
      disc /= 1 + m;
      sum += p * disc;
    }
    return sum;
  };
  let lo = 0;
  let hi = 1; // 100% APR ceiling
  if (pv(hi) > amountFinanced) return null;
  for (let iter = 0; iter < 200; iter++) {
    const mid = (lo + hi) / 2;
    if (pv(mid) > amountFinanced) lo = mid;
    else hi = mid;
  }
  return ((lo + hi) / 2) * 100;
}
